import React, { useCallback, useEffect } from 'react';
import { Task } from '@/types/tasks';

/**
 * Custom hook for managing task cleanup operations
 * Encapsulates cleanup logic following PRINCIPLE 5 and PRINCIPLE 6
 *
 * Only accepts setTasks since tasks array is accessed via function updater
 */
export const useTaskCleanup = (setTasks: React.Dispatch<React.SetStateAction<Task[]>>) => {
	// Extract cleanup logic following PRINCIPLE 6
	const cleanupExpiredTasks = useCallback(() => {
		const currentTime = Date.now();
		setTasks((prevTasks) => {
			const updatedTasks = prevTasks.filter(
				(task) => !task.expiresAt || (typeof task.expiresAt === 'number' && task.expiresAt > currentTime),
			);
			return updatedTasks;
		});
	}, [setTasks]);

	// Clear all tasks
	const clearAllTasks = useCallback(() => {
		setTasks((prevTasks) => (prevTasks.length === 0 ? prevTasks : []));
	}, [setTasks]);

	// Clear tasks by type
	const clearTasksByType = useCallback(
		(taskType: string) => {
			setTasks((prevTasks) => prevTasks.filter((task) => task.type !== taskType));
		},
		[setTasks],
	);

	// Clean up expired tasks - only for actual side effects (PRINCIPLE 2)
	useEffect(() => {
		const intervalId = setInterval(cleanupExpiredTasks, 1000 * 60); // Check every minute
		return () => clearInterval(intervalId);
	}, [cleanupExpiredTasks]);

	return {
		cleanupExpiredTasks,
		clearAllTasks,
		clearTasksByType,
	};
};
